import {
  useCallback,
  useRef,
  useEffect,
  useState,
  useMemo,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  useWindowSize,
  getCartCookies,
  onCheckoutBtnClick,
  getBacklinkUrl,
  SiteConfig,
  setCookieValue,
  useMount,
  onContinueShoppingClick,
} from '@utils';
import { useAppDispatch, useAppSelector } from '@store';
import './style.scss';

import {
  cartSelector, getCartData, removeCartItem,
  addPromotionToCart, removePromotionFromCart, updateProductQty, updateQuantity, UpdateProductQtyParams,
} from '@store/slices/cart';
import { ProductList } from '@components/ProductList';
import { EmptyCart } from '@components/EmptyCart';
import { OrderSummarySection } from '@components/OrderSummarySection';
import { InfoSection } from '@components/InfoSection';
import { Header } from '@components/common/Header';
import { Footer } from '@components/common/Footer';
import { Loader } from '@components/common/Loader';
import {
  CART_PATH,
  CONNECT_ACCESS_TOKEN_KEY,
  CONNECT_AUTH_URL,
  CONNECT_ID_TOKEN_KEY,
  SCREEN_SIZE,
} from '@config/constants';
import {
  authorize,
  fetchOpenIdConfiguration,
  isConfig, OpenIdConfiguration, SiteSpecificConfigKeys,
} from 'wly-ecommerce-common';
import { buildProceedToCheckoutRedirectUrl } from './utils/build-urls';
import { fetchTokens } from './utils/openIdConnect';
import { logError } from './utils/log';

const App = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const controllerRef = useRef<AbortController | null>(null);
  const [width] = useWindowSize();
  const [openIdConfig, setOpenIdConfig] = useState<OpenIdConfiguration>();
  const { siteId } = SiteConfig.getConfig();

  const isMobileScreen = useMemo(() => width < SCREEN_SIZE.MOBILE, [width]);
  const isSiteWithConnectAuth = useMemo(() => isConfig(SiteSpecificConfigKeys.connectAuthentication, siteId), [siteId]);

  const getCart = useCallback(() => {
    const cartCookies = getCartCookies();
    dispatch(getCartData(cartCookies));
  }, [dispatch]);

  useMount(getCart);

  useEffect(() => {
    const { connectAccessToken, connectIdToken } = getCartCookies();
    const hasConnectTokens = Boolean(connectAccessToken && connectIdToken);

    if (!isSiteWithConnectAuth || hasConnectTokens) {
      return;
    }

    (async () => {
      try {
        if (!openIdConfig) {
          const openIdConfigResponse = await fetchOpenIdConfiguration(CONNECT_AUTH_URL);
          setOpenIdConfig(openIdConfigResponse);
          return;
        }

        if (!openIdConfig?.token_endpoint) {
          logError('Missing token endpoint in OpenID configuration');
          return;
        }

        const tokens = await fetchTokens(openIdConfig.token_endpoint);
        if (!tokens) {
          logError('No tokens retrieved');
          return;
        }

        const oneHourFromNow = Date.now() / 1000 + 60 * 60;

        setCookieValue(CONNECT_ID_TOKEN_KEY, tokens.id_token, oneHourFromNow);
        setCookieValue(CONNECT_ACCESS_TOKEN_KEY, tokens.access_token, oneHourFromNow);

        getCart();
      } catch (error) {
        logError('Error fetching tokens:', error);
      }
    })();
  }, [openIdConfig, getCart, isSiteWithConnectAuth]);

  const {
    isLoading, products, marketRestrictedProducts, orderInfo, currency, promoCode, discounts, isFreeTrial,
  } = useAppSelector(cartSelector);
  const isEmptyCart = !products.length;

  const updateCartItemRequest = useCallback(({ uid, qty }: UpdateProductQtyParams) => {
    if (controllerRef.current) {
      controllerRef.current.abort();
    }

    controllerRef.current = new AbortController();

    dispatch(updateProductQty({ uid, qty, controller: controllerRef.current }));
  }, [dispatch]);

  const onQuantityChange = (uid: string) => (qty: number) => {
    dispatch(updateQuantity({ uid, qty })); // UI
    updateCartItemRequest({ uid, qty }); // API
  };

  const onRemove = (uid: string) => () => {
    dispatch(removeCartItem(uid));
  };
  const hasAllDigitalProducts = products.every(
    ({ digitalProduct }) => digitalProduct,
  );
  const handleContinueShopping = () => {
    onContinueShoppingClick('orderSummary');
    const continueShoppingUrl = getBacklinkUrl();
    setTimeout(() => window.location.replace(continueShoppingUrl), 100);
  };

  const handleProceedToCheckout = async () => {
    const { isAigateway, locale } = SiteConfig.getConfig();
    const { ecomToken } = getCartCookies();
    const isAuth = Boolean(ecomToken);

    onCheckoutBtnClick(products, currency, promoCode?.value);

    if (isSiteWithConnectAuth && !isAuth) {
      const oidcConfig = {
        issuer: CONNECT_AUTH_URL,
        clientId: 'viax',
        redirectUri: isAigateway
          ? `${window.location.origin}/${locale}${CART_PATH}`
          : `${window.location.origin}${CART_PATH}`,
        scope: 'openid alm_identity_ids',
      };

      if (!openIdConfig?.authorization_endpoint) {
        logError('Missing authorization endpoint in OpenID configuration');
        return;
      }

      await authorize(openIdConfig?.authorization_endpoint, oidcConfig);

      return;
    }

    const redirectUrl = buildProceedToCheckoutRedirectUrl();
    window.location.replace(redirectUrl);
  };

  const onPromoCodeDelete = async (prmId: string | undefined) => {
    if (!prmId) return;
    const cartCookies = getCartCookies();
    await dispatch(removePromotionFromCart({ cartId: cartCookies.cartId }));
  };

  const onPromoCodeApply = async (value: string, cb: () => void) => {
    const cartCookies = getCartCookies();

    // remove existing promo code
    if (promoCode) await onPromoCodeDelete(promoCode.value);

    const result = await dispatch(addPromotionToCart({ cartId: cartCookies.cartId, prmId: value }));
    // if the promo code is successfully applied, it will show the applied promo code instead of ‘input’
    if (!('error' in result)) cb();
  };

  if (isEmptyCart) {
    return (
      <>
        <Header />
        <div className="main-container">
          {isLoading
            ? <div className="cart-page--loader"><Loader /></div>
            : <EmptyCart />}
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="main-container">
        <div className="cart-page--container">
          {
            isMobileScreen
            && (
              <section className="cart-page__summary--mobile">
                <OrderSummarySection
                  onContinueShoppingBtnClick={handleContinueShopping}
                  onProceedToCheckoutBtnClick={handleProceedToCheckout}
                  orderInfo={orderInfo}
                  isLoading={isLoading}
                  currency={currency}
                  isFreeTrial={isFreeTrial}
                  hasAllDigitalProducts={hasAllDigitalProducts}
                  onPromoCodeApply={onPromoCodeApply}
                  onPromoCodeDelete={onPromoCodeDelete}
                />
              </section>
            )
          }
          <section className="product-list">
            <h2 className="product-list__heading">{t('cart.productList.title')}</h2>
            <ProductList
              isLoading={isLoading}
              isFreeTrial={isFreeTrial}
              products={products}
              discounts={discounts}
              marketRestrictedProducts={marketRestrictedProducts}
              onQuantityChange={onQuantityChange}
              currency={currency}
              onRemove={onRemove}
            />
          </section>
          <section className="cart-page__info">
            {
              !isMobileScreen
              && (
                <OrderSummarySection
                  onContinueShoppingBtnClick={handleContinueShopping}
                  onProceedToCheckoutBtnClick={handleProceedToCheckout}
                  orderInfo={orderInfo}
                  isLoading={isLoading}
                  currency={currency}
                  isFreeTrial={isFreeTrial}
                  hasAllDigitalProducts={hasAllDigitalProducts}
                  onPromoCodeApply={onPromoCodeApply}
                  onPromoCodeDelete={onPromoCodeDelete}
                />
              )
            }
            <InfoSection />
          </section>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default App;
